import React, { useEffect, useState } from 'react'
import { cn } from '@duck/ui'

interface OrgAvatarProps {
    org: { name: string; logoSquareURL?: string | null }
    size?: 'sm' | 'md' | 'lg'
    className?: string
}

const sizeClasses = {
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-12 h-12 text-base',
}

/**
 * Derives up to two characters from the word starts of an org name.
 * Examples: "Acme Corp" → "AC", "Stripe" → "S", "  " → "".
 */
function getOrgInitials(name: string): string {
    const trimmed = name.trim()
    if (!trimmed) return ''
    return trimmed
        .split(/\s+/)
        .slice(0, 2)
        .map((word) => word.charAt(0))
        .join('')
        .toUpperCase()
}

export const OrgAvatar: React.FC<OrgAvatarProps> = ({
    org,
    size = 'sm',
    className = '',
}) => {
    const logo = org.logoSquareURL
    const [imageError, setImageError] = useState(false)

    useEffect(() => {
        setImageError(false)
    }, [logo])

    const initials = getOrgInitials(org.name)

    if (logo && !imageError) {
        return (
            <img
                src={logo}
                alt={org.name}
                onError={() => setImageError(true)}
                className={cn(
                    sizeClasses[size],
                    'rounded object-contain',
                    className
                )}
            />
        )
    }

    return (
        <div
            role="img"
            aria-label={org.name}
            className={cn(
                sizeClasses[size],
                'rounded bg-accent/80 flex items-center justify-center font-medium text-accent-foreground',
                className
            )}
        >
            {initials || '?'}
        </div>
    )
}
